import { capitalize } from '@core/util/string';
import { Cell, Row } from './table-generator.model';
import { ContextAccessors } from '@models/matter';

export const RowTypes = {
	HEADER: 'header',
	BODY: 'body',
	FOOTER: 'footer',
};

// Define the properties for the table
export interface IAddTableProps {
	borderColor: string;
	borderWidth: string;
	zebraStripe: boolean;
	numOfColumns: number;
	selectedContext: string;
	headerRow: { cells: Cell[] };
	bodyRows: { cells: Cell[] }[];
	footerRows: { cells: Cell[] }[];
	columnWidths: number[];
	bgColor: string;
	showHeader: boolean;
	headerBG: boolean;
	forPreview?: boolean;
	headerHeight?: number;
	bodyHeight?: number;
	footerHeight?: number;
}

// Function to sanitize the row text
export const sanitizeRowText = (
	text: string,
	removeHeight?: boolean,
): { text: string; styles: string } => {
	const modifiedText = text.trim();

	const pTagRegex = new RegExp('</p>', 'g');
	const pTagMatches = text.match(pTagRegex);

	if (pTagMatches && pTagMatches.length > 1) {
		return { text, styles: '' };
	}

	// Regular expression to match a single <p> tag with or without style
	const singlePTag = /^<p(\s+style="[^"]+")?>(.*)<\/p>$/;

	const matches = modifiedText.match(singlePTag);
	if (matches) {
		// matches[1] would have the style attribute (if it exists)
		// matches[2] would have the inner content of the p tag
		let styles =
			matches[1]?.trim().replace('style="', '').replace(';"', ';') || '';

		if (removeHeight) {
			styles = styles.replace(/height:[^;]+;?/g, '');
		}
		return { text: matches[2], styles };
	}

	// If the text didn't match the pattern, return the original text
	return { text: modifiedText, styles: '' };
};

// Function to calculate the width and colspan for the cell
export const calculateCellDetails = (
	cell: Cell,
	index: number,
	columnWidths: number[],
	widthsIndex: number,
): { width: number; colspan: string; newWidthIndex: number } => {
	let newWidthIndex = widthsIndex;
	let width = columnWidths[newWidthIndex];
	const colspan = cell.colspan > 1 ? `colspan="${cell.colspan}" ` : ' ';

	if (cell.colspan > 1) {
		width = columnWidths
			.slice(index, index + cell.colspan)
			.reduce((a, b) => Number(a) + Number(b), 0);
		newWidthIndex += cell.colspan;
	} else {
		newWidthIndex++;
	}

	return { width, colspan, newWidthIndex };
};

// Function to get the cell background color
export const getCellBgColor = (
	zebraStripe: boolean,
	bgColorStyle: string,
): string => {
	return zebraStripe ? '' : bgColorStyle;
};

// Function to create the header for the table
export const createHeader = (
	headerRow: { cells: Cell[] },
	showHeader: boolean,
	bgColorStyle: string,
	borderStyle: string,
	columnWidths: number[],
	headerBG: boolean,
	forPreview?: boolean,
	headerHeight?: number,
): string => {
	let header = '';
	let widthsIndex = 0;

	if (headerRow && showHeader) {
		header = `<tr data-attribute='header'>`;
		const htmlTag = headerBG && !forPreview ? 'th' : 'td';

		headerRow.cells.forEach((cell, index) => {
			const { width, colspan, newWidthIndex } = calculateCellDetails(
				cell,
				index,
				columnWidths,
				widthsIndex,
			);
			widthsIndex = newWidthIndex;

			let style = `${borderStyle} ${width && `width:${width}%;`}`;
			if (headerBG) {
				style = `background-color:#f3f3f3; ${style}`;
			}
			if (bgColorStyle) {
				style = `${bgColorStyle} ${style}`;
			}

			const { styles, text } = sanitizeRowText(cell.text, true);

			if (headerHeight && headerHeight >= 1) {
				style = `height:${headerHeight}px; ${style}`;
			}

			style = `${style} ${styles}`;

			header += `<${htmlTag} ${colspan}style='${style}'>${text}</${htmlTag}>`;
		});

		header += '</tr>';
	}

	return header;
};

// Function to create the body for the table
export const createBody = (
	bodyRows: { cells: Cell[] }[],
	zebraStripe: boolean,
	bgColorStyle: string,
	borderStyle: string,
	columnWidths: number[],
	bodyHeight?: number,
): string => {
	let body = '';

	if (bodyRows && bodyRows.length > 0) {
		bodyRows.forEach((row) => {
			let widthsIndex = 0;
			body += `<tr data-attribute='body'>`;

			row.cells.forEach((cell, index) => {
				const { width, colspan, newWidthIndex } = calculateCellDetails(
					cell,
					index,
					columnWidths,
					widthsIndex,
				);
				widthsIndex = newWidthIndex;

				const { styles, text } = sanitizeRowText(cell.text, true);

				let style = `${getCellBgColor(zebraStripe, bgColorStyle)} ${borderStyle}`;

				if (width) {
					style += ` width:${width}%;`;
				}

				if (bodyHeight && bodyHeight >= 1) {
					style += ` height:${bodyHeight}px;`;
				}

				style += ` ${styles}`;

				body += `<td ${colspan}style='${style}'>${text}</td>`;
			});

			body += '</tr>';
		});
	}

	return body;
};

// Function to create the footer for the table
export const createFooter = (
	footerRows: { cells: Cell[] }[],
	bgColorStyle: string,
	borderStyle: string,
	columnWidths: number[],
	footerHeight?: number,
): string => {
	let footer = '';
	let widthsIndex = 0;

	if (footerRows && footerRows.length > 0) {
		footerRows.forEach((row) => {
			footer += `<tr data-attribute='footer'>`;

			row.cells.forEach((cell, index) => {
				const { width, colspan, newWidthIndex } = calculateCellDetails(
					cell,
					index,
					columnWidths,
					widthsIndex,
				);
				widthsIndex = newWidthIndex;

				const { styles, text } = sanitizeRowText(cell.text, true);

				let style = `${bgColorStyle} ${borderStyle}`;

				if (width) {
					style += ` width:${width}%;`;
				}

				if (footerHeight && footerHeight > 1) {
					style += ` height:${footerHeight}px;`;
				}

				style += ` ${styles}`;

				footer += `<td ${colspan}style='${style}'>${text}</td>`;
			});

			footer += '</tr>';
		});
	}

	return footer;
};

// Function to create the table using the header, body, and footer
export const addTable = (props: IAddTableProps): string => {
	const {
		borderColor,
		borderWidth,
		zebraStripe,
		numOfColumns,
		selectedContext,
		headerRow,
		bodyRows,
		footerRows,
		columnWidths,
		bgColor,
		showHeader,
		headerBG,
		forPreview,
		headerHeight,
		bodyHeight,
		footerHeight,
	} = props;

	let borderClass = '';
	if (borderColor && borderWidth && borderWidth !== '0px') {
		borderClass = `borders_${borderWidth.replace('.', '')}`;
	}

	let table = `<table class='${zebraStripe ? 'table_zebra_striped ' : ''}${
		borderClass ? `${borderClass} ` : 'noBorders '
	}mceNonEditable smart-table' style='width: 100%'>`;

	const borderStyle =
		borderColor && borderClass ? `border: 1px solid ${borderColor};` : '';
	const bgColorStyle = bgColor ? `background-color: ${bgColor};` : '';

	if (selectedContext) {
		table += `<tr><td colspan='${numOfColumns}'>Context: ${capitalize(selectedContext)}</td></tr>`;
	}

	const header = createHeader(
		headerRow,
		showHeader,
		bgColorStyle,
		borderStyle,
		columnWidths,
		headerBG,
		forPreview,
		headerHeight,
	);
	const body = createBody(
		bodyRows,
		zebraStripe,
		bgColorStyle,
		borderStyle,
		columnWidths,
		bodyHeight,
	);
	const footer = createFooter(
		footerRows,
		bgColorStyle,
		borderStyle,
		columnWidths,
		footerHeight,
	);

	table += `${header}${body}${footer}</table>`;

	return table;
};

interface IReverseAddTableResult {
	zebraStripe: boolean;
	border: boolean;
	borderColor: string;
	borderWidth: string;
	bgColor: string;
	showHeader: boolean;
	headerRow: Row;
	bodyRows: Row[];
	numOfColumns: number;
	footerRows: Row[];
	selectedContext: string;
	columnWidths: number[];
	headerBG: boolean;
	headerHeight?: number;
	bodyHeight?: number;
	footerHeight?: number;
}

/**
 * Parse styles and exclude specific properties.
 * @param {string} styleString - The inline style string.
 * @returns {string} - Filtered style string.
 */
const filterStyles = (styleString: string): string => {
	const propertiesToExclude = ['border', 'background-color', 'width'];
	const styles = styleString.split(';').filter((style) => {
		const [property] = style.split(':').map((s) => s.trim());
		return !propertiesToExclude.includes(property);
	});
	return styles.join(';');
};

/**
 * Parse HTML row elements to get cell text and colspan
 * @param {Element} row - The row element to be parsed.
 * @returns {Row} - An object with cell details.
 */
export const parseRow = (row: Element): Row => {
	return {
		cells: Array.from(row.querySelectorAll(':scope > th, :scope > td')).map(
			(cell) => {
				let innerContent = cell.innerHTML.trim();

				// Grab the inline styles from the cell and filter out unwanted styles.
				let inlineStyles = cell.getAttribute('style');
				if (inlineStyles) {
					inlineStyles = filterStyles(inlineStyles);
				}

				// Create a new <p> element and apply the styles.
				const pElement = document.createElement('p');
				if (inlineStyles) {
					pElement.setAttribute('style', inlineStyles);
				}
				pElement.innerHTML = innerContent;
				innerContent = pElement.outerHTML;

				return {
					text: innerContent,
					colspan: Number(cell.getAttribute('colspan')) || 1,
				};
			},
		),
	};
};

/**
 * Extract header row from the table rows.
 * @param {Element[]} rows - Array of HTML row elements.
 * @returns {[boolean, Row]} - A tuple with the boolean flag for hiding header and the header row details.
 */
export const extractHeader = (rows: Element[]): [boolean, Row] => {
	let showHeader = false,
		headerRow;

	if (rows.find((row) => row.getAttribute('data-attribute') === 'header')) {
		showHeader = true;
		headerRow = parseRow(
			rows.find((row) => row.getAttribute('data-attribute') === 'header'),
		);
	}

	return [showHeader, headerRow];
};

/**
 * Extract the context text from the table rows.
 * @param {Element[]} rows - Array of HTML row elements.
 * @returns {string} - The context text.
 */
export const extractContext = (rows: Element[]): string => {
	let selectedContext;
	const contextRow = rows.find((row) => {
		// This is not an exact match since scaping characters are added to the text. As an alternative, we are just looking to see if the text contains 'Context:'
		// This is just a workaround after this broke because of the editor update, we shouldn't be using a innerText to determine the context, attr.data makes more sense for this.
		return (row as HTMLElement).innerText.includes('Context:');
	});
	if (contextRow) {
		selectedContext = contextRow.textContent
			.slice(8)
			.trim() as ContextAccessors; // Remove 'Context:' prefix and trim spaces
	}

	return selectedContext;
};

export function extractBorderWidth(classList: DOMTokenList): string {
	let borderWidth;
	for (let i = 0; i < classList.length; i++) {
		const className = classList[i];
		const match = className.match(/^borders_([0-9]{1,2})(px)?$/);
		if (match) {
			borderWidth =
				match[1].length === 1
					? match[1] + 'px'
					: match[1].charAt(0) + '.' + match[1].charAt(1) + 'px';
			break;
		}
	}

	return borderWidth;
}

export function extractColors(table: HTMLElement): {
	borderColor: string;
	bgColor: string;
} {
	let borderColor;
	let bgColor;

	// Update query selector to get only rows with data-attribute="body"
	const rows = table.querySelectorAll('tr[data-attribute="body"]');

	if (rows.length > 0) {
		// Changed this from checking for > 1 to > 0 since we want to process even if there's only one such row.
		const firstCell =
			rows[0].querySelector('td') || rows[0].querySelector('th'); // Changed this to rows[0] since we're already filtering with the data-attribute condition.
		if (firstCell) {
			const styleAttr = firstCell.getAttribute('style');
			if (styleAttr) {
				const colorMatch = styleAttr.match(
					/border: [0-9a-z\s]*(#[0-9a-f]{6});/i,
				);
				if (colorMatch) {
					borderColor = colorMatch[1];
				}

				const bgMatch = styleAttr.match(/background-color: (#[0-9a-f]{6});/i);
				if (bgMatch) {
					bgColor = bgMatch[1];
				}
			}
		}
	}

	return { borderColor, bgColor };
}

export function checkIfHeaderBG(table: HTMLElement): boolean {
	const headerRow: HTMLElement | null = table.querySelector(
		'tr[data-attribute="header"]',
	);

	if (headerRow) {
		const firstCell: Element | null = headerRow.firstElementChild;

		if (firstCell && firstCell.tagName.toLowerCase() === 'th') {
			return true;
		}
	}

	return false;
}

export function getNumberOfColumns(
	bodyRows: Row[],
	footerRows: Row[],
	headerRow: Row,
	showHeader: boolean,
): number {
	const bodyColumns = bodyRows.map((row) =>
		row.cells.reduce((acc, cell) => acc + cell.colspan, 0),
	);
	const headerColumns = showHeader
		? headerRow.cells.reduce((acc, cell) => acc + cell.colspan, 0)
		: 0;
	const footerColumns = footerRows.map((row) =>
		row.cells.reduce((acc, cell) => acc + cell.colspan, 0),
	);
	const numOfColumns = Math.max(
		headerColumns,
		...bodyColumns,
		...footerColumns,
	);

	return numOfColumns;
}

/**
 * Reverse engineer the HTML table to get the table details.
 * @param {string} html - The HTML string of the table.
 * @returns {IReverseAddTableResult} - The details of the table.
 */
export function reverseAddTable(html: string): IReverseAddTableResult {
	const doc = new DOMParser().parseFromString(html, 'text/html');
	const table = doc.querySelector('table.smart-table') as HTMLElement;

	const { borderColor, bgColor } = extractColors(table);
	const borderWidth = extractBorderWidth(table.classList);

	const zebraStripe = table.classList.contains('table_zebra_striped');
	const border =
		table.classList.contains('borders') ||
		table.classList.contains('noBorders');
	const rows = Array.from(table.querySelectorAll('tr'));

	// Extract raw header, body, and footer rows
	const rawHeaderRow = rows.filter(
		(row) => row.getAttribute('data-attribute') === 'header',
	)[0];
	const rawBodyRows = rows.filter(
		(row) => row.getAttribute('data-attribute') === 'body',
	);
	const rawFooterRows = rows.filter(
		(row) => row.getAttribute('data-attribute') === 'footer',
	);

	const [showHeader, headerRow] = extractHeader(rows);
	const bodyRows = rawBodyRows.map((row) => parseRow(row));
	const footerRows = rawFooterRows.map((row) => parseRow(row));

	const numOfColumns = getNumberOfColumns(
		bodyRows,
		footerRows,
		headerRow,
		showHeader,
	);

	// Extract context
	const selectedContext = extractContext(rows);

	// Extract the column widths from all rows
	const allRowsWidths = [];

	if (showHeader && rawHeaderRow) {
		const headerRowWidths = extractRowWidths(rawHeaderRow);
		allRowsWidths.push(headerRowWidths);
	}

	rawBodyRows.forEach((row) => {
		const rowWidths = extractRowWidths(row);
		allRowsWidths.push(rowWidths);
	});

	rawFooterRows.forEach((row) => {
		const rowWidths = extractRowWidths(row);
		allRowsWidths.push(rowWidths);
	});

	// Identify the column widths from the row that has the most columns
	const columnWidths = allRowsWidths.reduce(
		(maxRow, currentRow) =>
			currentRow.length > maxRow.length ? currentRow : maxRow,
		[],
	);

	// Extract the heights of the header, body, and footer rows by their class name (ex: height:20px;)
	const headerHeight = extraRowHeight(rawHeaderRow);
	const bodyHeight = extraRowHeight(rawBodyRows[0]);
	const footerHeight = extraRowHeight(rawFooterRows[0]);

	return {
		zebraStripe,
		border,
		borderColor,
		borderWidth,
		bgColor,
		showHeader,
		headerRow,
		bodyRows,
		numOfColumns,
		footerRows,
		selectedContext,
		columnWidths,
		headerBG: checkIfHeaderBG(table),
		headerHeight: headerHeight,
		bodyHeight: bodyHeight,
		footerHeight: footerHeight,
	};
}

function extractRowWidths(row: HTMLElement): number[] {
	const widths = [];
	const cells = Array.from(row.querySelectorAll(':scope > th, :scope > td'));
	cells.forEach((cell) => {
		const cellElement = cell as HTMLElement;
		let width = Number(cellElement.style.width.replace('%', ''));
		if (cellElement.getAttribute('colspan')) {
			width = width / Number(cellElement.getAttribute('colspan'));
		}
		widths.push(width);
	});
	return widths;
}

function extraRowHeight(row: HTMLElement): number | undefined {
	if (row) {
		const cell = row.firstElementChild;
		if (cell) {
			const heightMatch = cell.outerHTML.match(/height:\s*([0-9]+)px/);
			if (heightMatch) {
				return Number(heightMatch[1]);
			}
		}
	}
}
